import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';

import * as Objects from '@core/objects';

import * as Access from '@app/models/access';

import { VersionsRequests } from './versions.requests';
import { NotificationsService } from '@core/services/notifications/notifications.service';
import { NotificationsContextEnum } from '@core/store/models/notifications.enum';
import { VersionFeedback, ShareVersionFeedback } from '@core/models/version-feedback';
import { VersionShare, UploadVersionShare, VersionShareType, VersionShareEntryDetail, isVersionShareOpen } from '@core/models/version-share.model';

import * as fromStore from './versions.state';
import * as fromActions from './versions.actions';

import * as  fromAppStore  from '../app/app.state'; 

@Injectable()
export class VersionsEffects {

    protected readonly PLUGIN_REQUEST_FEEDBACK = 'share_feedback';

    constructor(private actions$: Actions, private store$: Store<fromStore.Versions>,
        private appStore$: Store<fromAppStore.App>, private requests: VersionsRequests,
        private notifSrv: NotificationsService) {}

    
    public loadVersion$: Observable<fromActions.VersionsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadVersion>(fromActions.VersionsActionType.LoadVersion),
        switchMap((action) => {
            return this.requests.load$(action.versionId).pipe(
                
                map((response: HttpResponse<Objects.Version>) => {
                    if (response.status === 200) {
                        // Full success
                        return new fromActions.LoadVersionSuccess(response.body);
                    } else if (response.status === 206) {
                        // Partial content: User has limited access, set project header instead
                        return new fromActions.LoadPartialVersionSuccess(response.body);
                    }
                    // Fallback for unexpected status codes (shouldn't happen with catchError)
                    throw new Error(`Unexpected status: ${response.status}`);
                }),
                catchError((err: HttpErrorResponse) => {
                    console.log('Error loading project:', err);
                    return of(new fromActions.LoadVersionError(action.versionId,err));
                })
            );
        })
    ));

    
    public loadVersionSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadVersionSuccess>(fromActions.VersionsActionType.LoadVersionSuccess),
         //withLatestFrom(this.store$.select(fromStore.usersGetAllUsersSelector)),
        switchMap((action) => [
            new fromActions.LoadVersionAssets(),
            // new fromActions.LoadVersionMessages(),
            new fromActions.LoadVersionComments(),
            new fromActions.LoadEditorAccess(),
            new fromActions.LoadVersionProject(),
            // new fromActions.LoadVersionFeedbacks(),
            new fromActions.LoadVersionShares(),
        ])
    ));

    
    // public loadVersionError$ = createEffect(() => this.actions$.pipe(
    //     ofType<fromActions.LoadVersionError>(fromActions.VersionsActionType.LoadVersionError),
        
    //     switchMap((action) => {
            
    //         // if (action.error.status == 403)
    //         //     return [new fromActions.LoadVersionHeader(action.versionId)];
    //         // else 
    //             return []
    //     })
    // ));

    
    // public loadVersionHeader$:Observable<fromActions.VersionsActions> = createEffect(() => this.actions$.pipe(
    //     ofType<fromActions.LoadVersionHeader>(fromActions.VersionsActionType.LoadVersionHeader),
    //     switchMap((action) => {
    //         return this.requests.loadHeader$(action.versionId).pipe(
    //             map((header: Objects.Version) => {
    //                 return new fromActions.LoadVersionHeaderSuccess(header);
    //             }),
    //             catchError((err: any) => {
    //                 console.log(err);
    //                 throwError(() => err);
    //                 return of(new fromActions.LoadVersionHeaderError(err));
    //             })
    //         );
    //     })
    // ));

    
    public loadRelateds$: Observable<fromActions.VersionsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadRelatedVersions>(fromActions.VersionsActionType.LoadRelatedVersions),
        switchMap((action) => {
            return this.requests.loadRelateds$(action.link).pipe(
                map((versions: Objects.Version[]) => {
                    return new fromActions.LoadRelatedVersionsSuccess(versions);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadRelatedVersionsError(err));
                })
            );
        })
    ));

    
    public loadVersionAssets$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadVersionAssets>(fromActions.VersionsActionType.LoadVersionAssets),
        withLatestFrom(this.store$.select(fromStore.versionSelector)),
        switchMap(([action, version]) => {
            return this.requests.loadAssets$(version.id).pipe(
                map((assets: Array<Objects.AmsFile>) => {
                    return new fromActions.LoadVersionAssetsSuccess(assets);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadVersionAssetsError(err));
                })
            );
          
        })
    ));

    public LoadVersionFeedbackNotesCounter$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadVersionFeedbackNotesCounter>(fromActions.VersionsActionType.LoadVersionFeedbackNotesCounter),
        withLatestFrom(this.store$.select(fromStore.versionSelector)),
        mergeMap(([action, version]) => {
            return this.requests.loadFeedbackFileAnnotationsCounter$(version.id).pipe(
                map((counter: Array<Objects.AnnotationsCounter>) => {
                    return new fromActions.LoadVersionFeedbackNotesCounterSuccess(counter);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadVersionFeedbackNotesCounterError(err));
                })
            );
          
        })
    ));

    /**
     * @deprecated oct-2026: legacy share feedback url call, removed in the backend (answers 404).
     * Replaced by loadMyVersionShares$ (on LoadVersionSharesSuccess).
     */
    public loadShareVersionFeedback$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadShareFeedback>(fromActions.VersionsActionType.LoadShareFeedback),
        withLatestFrom(this.store$.select(fromStore.versionSelector)),
        switchMap(([action, version]) => {
            return this.requests.loadShareFeedback$(version.id, action.user).pipe(
                map((shareFeedbacks: ShareVersionFeedback[]) => {
                    return new fromActions.LoadShareFeedbackSuccess(version,shareFeedbacks);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadShareFeedbackError(err));
                })
            );
          
        })
    ));

    // oct-2026: the current user's entries are a subset of LoadVersionShares' list (same backend cache), which every
    // version load and every reload already requests, so they are taken from it instead of GET /shares?user=me.
    // The detail (share options and files, not in the list) is only requested for the open entries.
    public loadMyVersionShares$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadVersionSharesSuccess>(fromActions.VersionsActionType.LoadVersionSharesSuccess),
        withLatestFrom(
            this.appStore$.pipe(select(fromAppStore.appSessionSelector)),
            this.appStore$.pipe(select(fromAppStore.appPluginsAccessSelector(this.PLUGIN_REQUEST_FEEDBACK)))
        ),
        // without the plugin "my shares" were never loaded: keep it that way
        filter(([action, session, pluginAccess]) => !!pluginAccess && action.version != null),
        switchMap(([action, session, pluginAccess]) => {
            const userId = session?.current?.user?.id;
            const openShares = (action.shares ?? []).filter((vs: VersionShare) => vs?.entry?.user?.id === userId && isVersionShareOpen(vs));
            if (openShares.length == 0)
                return of(new fromActions.LoadMyVersionSharesSuccess([]));
            return forkJoin(openShares.map((vs: VersionShare) => this.requests.loadVersionShareEntry$(action.version.id, vs.entry.token))).pipe(
                map((entries: VersionShareEntryDetail[]) => new fromActions.LoadMyVersionSharesSuccess(entries)),
                catchError((err: HttpErrorResponse) => {
                    // an empty list is a success (not a recipient): only the failed detail calls are notified
                    if (err?.status == 403 || err?.status == 404) {
                        this.notifSrv.addNotification(NotificationsContextEnum.EVENT_FAILED, {
                            id: Math.random().toString(36).replace('0.', ''),
                            details: {
                                title: "Loading User Feedback",
                                message: "There was an error loading user feedback, please try again. If the error persists, please reach out to our support team through the Help Me button."
                            }
                        });
                    }
                    return of(new fromActions.LoadMyVersionSharesError(err));
                })
            );
        })
    ));

    public loadVersionContextComments$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadVersionComments>(fromActions.VersionsActionType.LoadVersionComments),
        withLatestFrom(this.store$.select(fromStore.versionSelector)),
        switchMap(([action, version]) => {
            return this.requests.loadComments$(version.id).pipe(
                map((comments: Array<Objects.VersionMessage>) => {
                    return new fromActions.LoadVersionCommentsSuccess(comments);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadVersionCommentsError(err));
                })
            );
          
        })
    ));

    
    public loadVersionProject$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadVersionProject>(fromActions.VersionsActionType.LoadVersionProject),
        withLatestFrom(this.store$.select(fromStore.versionSelector)),
        switchMap(([action, version]) => {
            return this.requests.loadProject$(version.id).pipe(
                map((project: Objects.Request) => {
                    return new fromActions.LoadVersionProjectSuccess(project);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadVersionProjectError(err));
                })
            );
          
        })
    ));

    /**
     * Refresh individual file signature by fetching latest comments
     * and extracting the signature for the specific file UUID
     */
    public refreshCommentsSignature$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.RefreshCommentsSignature>(fromActions.VersionsActionType.RefreshCommentsSignature),
        withLatestFrom(this.store$.select(fromStore.versionSelector)),
        switchMap(([action, version]) => {
            return this.requests.refreshCommentsAttachmentsSignatures$(version.id).pipe(
                map((comments: Array<Objects.VersionMessage>) => {
                    // Find the file with matching UUID in the comments
                    let foundAccess: Objects.AmsFileAccess = null;
                    
                    for (const comment of comments) {
                        if (comment.message && comment.message.attachments) {
                            for (const file of comment.message.attachments) {
                                if (file.uuid === action.fileUuid && file.access) {
                                    foundAccess = file.access;
                                    break;
                                }
                            }
                        }
                        if (foundAccess) break;
                    }
                    
                    if (foundAccess) {
                        return new fromActions.RefreshCommentsSignatureSuccess(action.fileUuid, foundAccess);
                    } else {
                        return new fromActions.RefreshCommentsSignatureError(action.fileUuid, new HttpErrorResponse({ status: 404, statusText: 'File not found in comments' }));
                    }
                }),
                catchError((err: HttpErrorResponse) => {
                    return of(new fromActions.RefreshCommentsSignatureError(action.fileUuid, err));
                })
            );
        })
    ));

    // @deprecated oct-2026: legacy share feedback bubbles url call (GET /p/feedback/versions/feedbacks/{ids}), removed in the backend (answers 404). Use version_shares (LoadVersionShares)
    public loadVersionFeedbacks$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadVersionFeedbacks>(fromActions.VersionsActionType.LoadVersionFeedbacks),
        withLatestFrom(this.store$.select(fromStore.versionSelector),this.appStore$.pipe(select(fromAppStore.appPluginsAccessSelector(this.PLUGIN_REQUEST_FEEDBACK)))),
        switchMap(([action, version,pluginAccess]) => {
            if(pluginAccess){
                return this.requests.loadFeedbacks$(version.id).pipe(
                    map((feedbacks: Array<VersionFeedback>) => {
                        // return new fromActions.LoadVersionFeedbacksPreSuccess(feedbacks); // if we want to pre proccess feedback status
                        return new fromActions.LoadVersionFeedbacksSuccess(feedbacks);
                    }),
                    catchError((err: any) => {
                        console.log(err);
                        throwError(() => err);
                        return of(new fromActions.LoadVersionFeedbacksError(err));
                    })
                );
            } else { // no load feedbacks if no plugin access
                return [];
            }
          
        })
    ));
    public loadVersionShares$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadVersionShares>(fromActions.VersionsActionType.LoadVersionShares),
        withLatestFrom(this.store$.select(fromStore.versionSelector),this.appStore$.pipe(select(fromAppStore.appPluginsAccessSelector(this.PLUGIN_REQUEST_FEEDBACK)))),
        switchMap(([action, version, pluginAccess]) => {

            if(pluginAccess){
                return this.requests.loadVersionShares$(version.id).pipe(
                    map((shares: Array<VersionShare>) => {
                        return new fromActions.LoadVersionSharesSuccess(shares, version);
                    }),
                    catchError((err: any) => {
                        console.log(err);
                        throwError(() => err);
                        return of(new fromActions.LoadVersionSharesError(err));
                    })
                );
            } else {
                return [new fromActions.LoadVersionSharesSuccess([], version)]; // no load version shares if no plugin access
            }
          
        })
    ));

    public loadAllVersionShares$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadVersionSharesSuccess>(fromActions.VersionsActionType.LoadVersionSharesSuccess),
        withLatestFrom(this.store$.select(fromStore.versionVersionSharesSelector)),
        mergeMap(([action, versionShares]) => {
            let lsEvents = [];
            versionShares.map((sv: VersionShare) => {
                if(sv.type == VersionShareType.UPLOAD_VERSION_TYPE){
                    if(sv.options?.id_version != null){
                        // load the version uploaded we get in the options
                        lsEvents.push(new fromActions.LoadUploadedVersion(sv.options.id_version));
                    }
                }
            });

            return lsEvents;
        })
    ));

    public loadUploadedVersion$: Observable<fromActions.VersionsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadUploadedVersion>(fromActions.VersionsActionType.LoadUploadedVersion),
        switchMap((action) => {
            return this.requests.load$(action.versionId).pipe(
                map((response: HttpResponse<Objects.Version>) => {
                    return new fromActions.LoadUploadedVersionSuccess(response.body);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadUploadedVersionError(action.versionId,err));
                })
            );
        })
    ));

    // Load UploadVersionShares to gent origin version for the uplaod-version origin.
    public loadUploadVersionShares$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadUploadVersionShares>(fromActions.VersionsActionType.LoadUploadVersionShares),
        mergeMap((action) => {
            return this.requests.getAllUploadVersionShares$(action.token).pipe(
                map((shares: Array<UploadVersionShare>) => {
                    // Use effectiveContext to decide if is user or all users
                    const effectiveContext = action.context || fromStore.UPLOAD_VERSION_SHARES_CONTEXT.current_user;
                    return new fromActions.LoadUploadVersionSharesSuccess(shares,effectiveContext);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadUploadVersionSharesError(err));
                })
            );
          
        })
    ));

    // effect to perform action to archive a feedback
    // public archiveVersionFeedback$: Observable<fromActions.VersionsActions> = createEffect(() => this.actions$.pipe(
    //     ofType<fromActions.ArchiveVersionFeedback>(fromActions.VersionsActionType.ArchiveVersionFeedback),
    //     withLatestFrom(this.store$.select(fromStore.versionSelector)),
    //     mergeMap(([action,version]) => {
    //         return this.requests.archiveFeedback$(version, action.feedback).pipe(
    //             map(() => {
    //                 return new fromActions.ArchiveVersionFeedbackSuccess(action.feedback);
    //             }),
    //             catchError((err: any) => {
    //                 console.log(err);
    //                 throwError(() => err);
    //                 return of(new fromActions.ArchiveVersionFeedbackError(err));
    //             })
    //         );
    //     })
    // ));

    // dec-2023 => effect to perform action to archive a share version by token
    public archiveVersionShare$: Observable<fromActions.VersionsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.ArchiveVersionShare>(fromActions.VersionsActionType.ArchiveVersionShare),
        withLatestFrom(this.store$.select(fromStore.viewSelector)),
        mergeMap(([action,viewVersion]) => {
            return this.requests.archiveVersionShare$(viewVersion.version, action.share,viewVersion.project).pipe(
                map(() => {
                    return new fromActions.ArchiveVersionShareSuccess(action.share);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.ArchiveVersionShareError(err));
                })
            );
        })
    ));

    // needed for reload shares after archive one
    // public archiveVersionShareSuccess$ = createEffect(() => this.actions$.pipe(
    //     ofType<fromActions.ArchiveVersionShareSuccess>(fromActions.VersionsActionType.ArchiveVersionShareSuccess),
    //     switchMap((action) => {
    //         return [new fromActions.LoadVersionShares()];
    //     })
    // ));

    // apr-2024 => effect to perform action to reset a share version by token
    public resetVersionShare$: Observable<fromActions.VersionsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.ResetVersionShare>(fromActions.VersionsActionType.ResetVersionShare),
        withLatestFrom(this.store$.select(fromStore.viewSelector)),
        mergeMap(([action,viewVersion]) => {
            return this.requests.resetVersionShare$(viewVersion.version,action.share,viewVersion.project).pipe(
                map(() => {
                    return new fromActions.ResetVersionShareSuccess(action.share);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.ResetVersionShareError(err));
                })
            );
        })
    ));

    // both reset and archive success effects not needed 27th may 2024 <diego@twonas.com>
    // reload shares after reset one
    // public resetVersionShareSuccess$ = createEffect(() => this.actions$.pipe(
    //     ofType<fromActions.ResetVersionShareSuccess>(fromActions.VersionsActionType.ResetVersionShareSuccess),
    //     switchMap((action) => {
    //         return [new fromActions.LoadVersionShares()];
    //     })
    // ));
        
    // reload shares after reset error to take feedabck to the original status
    // public resetVersionShareError$ = createEffect(() => this.actions$.pipe(
    //     ofType<fromActions.ResetVersionShareError>(fromActions.VersionsActionType.ResetVersionShareError),
    //     switchMap((action) => {
    //         return [new fromActions.LoadVersionShares()];
    //     })
    // ));
        

    public loadEditorAccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadEditorAccess>(fromActions.VersionsActionType.LoadEditorAccess),
        withLatestFrom(this.store$.select(fromStore.versionSelector)),
        switchMap(([action, version]) => {
            return this.requests.loadAccess$(version.id).pipe(
                map((access: Access.AccessVersion) => {
                    return new fromActions.LoadEditorAccessSuccess(access);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadEditorAccessError(err));
                })
            );
          
        })
    ));

    
    public setVersionStatus$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetVersionStatus>(fromActions.VersionsActionType.SetVersionStatus),
        withLatestFrom(this.store$.select(fromStore.versionSelector)),
        switchMap(([action, version]) => {
            return this.requests.setStatus$(version.id, action.status).pipe(
                map((version: Objects.Version) => {
                    return new fromActions.SetVersionStatusSuccess(version);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SetVersionStatusError(err));
                })
            );
          
        })
    ));

    
    public setVersionStatusSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetVersionStatusSuccess>(fromActions.VersionsActionType.SetVersionStatusSuccess),
         //withLatestFrom(this.store$.select(fromStore.usersGetAllUsersSelector)),
        switchMap((action) => [
            new fromActions.LoadEditorAccess(),
        ])
    ));

    
    public setVersionStatusError$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetVersionStatusError>(fromActions.VersionsActionType.SetVersionStatusError),
        withLatestFrom(this.store$.select(fromStore.versionSelector)),
        switchMap(([action, version]) => [
            new fromActions.LoadVersion(version.id)
        ])
    ));

    
    public setVersionLabels$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetVersionLabels>(fromActions.VersionsActionType.SetVersionLabels),
        withLatestFrom(this.store$.select(fromStore.versionSelector)),
        switchMap(([action, version]) => {
            return this.requests.setLabels$(version.id, action.labels).pipe(
                map((version: Objects.Version) => {
                    return new fromActions.SetVersionLabelsSuccess(version);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SetVersionLabelsError(err));
                })
            );
          
        })
    ));

    
    public setVersionLabelsSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetVersionLabelsSuccess>(fromActions.VersionsActionType.SetVersionLabelsSuccess),
         //withLatestFrom(this.store$.select(fromStore.usersGetAllUsersSelector)),
        switchMap((action) => [
            // new LoadEditorAssigners(),
            // new LoadEditorAccess(),
        ])
    ));

    
    public setVersionLabelsError$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetVersionLabelsError>(fromActions.VersionsActionType.SetVersionLabelsError),
        withLatestFrom(this.store$.select(fromStore.versionSelector)),
        switchMap(([action, version]) => [
            new fromActions.LoadVersion(version.id)
        ])
    ));

    public sendNewUploadVersionFiles$: Observable<fromActions.VersionsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SendUploadVersionFiles>(fromActions.VersionsActionType.SendUploadVersionFiles),
        withLatestFrom(this.store$.select(fromStore.versionSelector)),
        switchMap(([action, version]) => {
            return this.requests.submitNewVersionFiles$(action.token,action.files,version).pipe(
                map((response:any) => {
                    return new fromActions.SendUploadVersionFilesSuccess();
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SendUploadVersionFilesError(err));
                })
            );
        })
    ));

    /*******  Effects for WS events  **********/
    public updateVersion$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UpdateVersion>(fromActions.VersionsActionType.UpdateVersion),
        withLatestFrom(this.store$.select(fromStore.viewSelector)),
        switchMap(([action, view]) => {
            let events = [];
            if(view.id === action.versionId)
                events.push(new fromActions.LoadVersion(action.versionId));
            
            return events;
            
        })
    ));

    // update version messages for the loaded id in store
    public updateVersioMessages$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UpdateVersionMessages>(fromActions.VersionsActionType.UpdateVersionMessages),
        withLatestFrom(this.store$.select(fromStore.viewSelector)),
        switchMap(([action, view]) => {
            let events = [];
            if(view.id === action.versionId){
                events.push(new fromActions.LoadVersionComments());
            }
            
            return events;
            
        })
    ));

    // update version feedbacks for the loaded id in store
    // public updateVersioFeedbacks$ = createEffect(() => this.actions$.pipe(
    //     ofType<fromActions.UpdateVersionFeedbacks>(fromActions.VersionsActionType.UpdateVersionFeedbacks),
    //     withLatestFrom(this.store$.select(fromStore.viewSelector)),
    //     switchMap(([action, view]) => {
    //         let events = [];
    //         if(view.id === action.versionId){
    //             events.push(new fromActions.LoadShareFeedback(action.user));
    //             events.push(new fromActions.LoadVersionFeedbacks());
    //         }
            
    //         return events;
            
    //     })
    // ));

    // update version shares and uploadVersionShares for the loaded version id in store
    public updateVersioShares$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UpdateVersionShares>(fromActions.VersionsActionType.UpdateVersionShares),
        withLatestFrom(this.store$.select(fromStore.viewSelector)),
        switchMap(([action, view]) => {
            let events = [];
            if(view.id === action.versionId){
                // events.push(new fromActions.LoadShareFeedback(action.user));
  //              events.push(new fromActions.LoadVersionFeedbacks());
                events.push(new fromActions.LoadVersionShares());
                events.push(new fromActions.LoadVersionComments());
                events.push(new fromActions.LoadVersionFeedbackNotesCounter());
            }
            
            return events;
            
        })
    ));
    
    public sendSelfShareFeedbackRequest$: Observable<fromActions.VersionsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SendSelfShareFeedbackRequest>(fromActions.VersionsActionType.SendSelfShareFeedbackRequest),
        withLatestFrom(this.store$.select(fromStore.versionAssetsSelector)),
        switchMap(([action, files]) => {
            const payload = { ...action.options, files: files.map((f: Objects.AmsFile) => f.id)};
            return this.requests.submitNewVersionFeedback$(payload).pipe(
                map((response:any) => {
                    return new fromActions.SendSelfShareFeedbackRequestSuccess();
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SendSelfShareFeedbackRequestError(err));
                })
            );
        })
    ));


    /**
     * @deprecated once we have status always when we get feedbacks
     */
    // public loadVersionFeedbacksPreSuccess$: Observable<fromActions.VersionsActions> = createEffect(() => this.actions$.pipe(
    //     ofType<fromActions.LoadVersionFeedbacksPreSuccess>(fromActions.VersionsActionType.LoadVersionFeedbacksPreSuccess),
    //     switchMap((action) => {
    //         let newStatus: VersionFeedbackStatus;
    //         let feedbacks = action.feedbacks.map((fv: VersionFeedback) => {
    //             // check if we have feedback status, if not then check approval
    //             if(fv?.status == null){
    //                 // set new default status
    //                 newStatus = {date:'',status_code : FeedbackStatusCodeEnum.OPENED_CODE, status_text: FeedbackStatusTextEnum.OPENED_TEXT};
    //                 // check approval not null
    //                 if(fv.approval != null){
    //                     newStatus = {
    //                         date:  fv.approval.date,
    //                         status_code: (fv.approval.status_code != ApprovalStatusCodeEnum.PENDING_CODE) ? 
    //                             FeedbackStatusCodeEnum.CLOSED_CODE : FeedbackStatusCodeEnum.OPENED_CODE,
    //                         status_text: (fv.approval.status_code != ApprovalStatusCodeEnum.PENDING_CODE) ? 
    //                             FeedbackStatusTextEnum.CLOSED_TEXT : FeedbackStatusTextEnum.OPENED_TEXT
    //                     }
    //                 }
    //                 // if approval is null && status null new status is the previous set as open.
    //                 return {...fv, status: newStatus};
    //             } else {
    //                 return fv;
    //             }
    //         });
    //         return [new fromActions.LoadVersionFeedbacksSuccess(feedbacks)];
    //     })
    // ));
}
